import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { LuCalendar, LuUser, LuClock, LuUsers, LuLayoutGrid, LuBaby, LuAccessibility, LuAlertTriangle, LuCheck, LuDog } from "react-icons/lu";
import { StrollerSvgIcon } from "../../icons/SvgIcons";
import DiningTableIcon from "../../icons/DiningTableIcon";
import { PhoneSvgIcon, NoPhoneSvgIcon, NoPhoneTwoLinesSvgIcon } from "../../icons/SvgIcons";
import { getReservationStatusAbreviationTranslation } from "../ReservationStatus";
import { DefaultDateUpper, DefaultDateWeekday, statusToGroup, iconStatusMap } from "../DefaultImports";
import Tooltip from "../ToolTipWrapper";
const normalizeText = value => String(value || "").trim().toLowerCase();
const normalizeCssColor = (value, fallback = "#99B0AD") => {
  const color = String(value || "").trim();
  if (!color) return fallback;
  if (color.startsWith("#")) return color;
  if (/^[0-9A-Fa-f]{6}$/.test(color)) return `#${color}`;
  return color;
};
const getStatusGroup = status => {
  if (!status) return null;
  if (statusToGroup?.[status]) {
    return statusToGroup[status];
  }
  const normalizedStatus = normalizeText(status);
  const matchedStatusKey = Object.keys(statusToGroup || {}).find(key => normalizeText(key) === normalizedStatus);
  if (matchedStatusKey) {
    return statusToGroup[matchedStatusKey];
  }
  return status;
};
const getStatusGroupColor = (status, fallback = "#99B0AD") => {
  const group = getStatusGroup(status);
  const color = iconStatusMap?.[group]?.color;
  return normalizeCssColor(color, fallback);
};
const BEFORE_COLOR = getStatusGroupColor("accepted", "#99B0AD");
const AFTER_COLOR = getStatusGroupColor("late", "#EE754C");
const CHANGE_BACKGROUND = "#FAF6E9";

// Styled Components
const Card = styled.div`
  background: #fff;
  font-size: ${({ $compact }) => ($compact ? "12px" : "15px")};
  width: 100%;

  svg {
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    stroke-width: 1.75;
  }

  ${({ $compact }) =>
    $compact &&
    `
      background: transparent;
      min-width: 0;
    `}
`;
const Box = styled.div`
  display: flex;
  flex-direction: ${({ $compact }) => ($compact ? "row" : "column")};
  flex-wrap: ${({ $compact }) => ($compact ? "wrap" : "initial")};
  align-items: ${({ $compact }) => ($compact ? "center" : "stretch")};
  gap: ${({ $compact }) => ($compact ? "4px 10px" : "3px")};
  padding: ${({ $compact }) => ($compact ? "6px 10px" : "3px")};
  min-width: 0;
  overflow: visible;
`;
const BoxRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  ${({ $compact }) =>
    $compact &&
    `
      display: contents;
    `}
`;
const BoxItems = styled.div`
  display: flex;
  align-items: center;
  margin: 2px 0px;
  color: ${BEFORE_COLOR};
  gap: 4px;

  ${({ $compact }) =>
    $compact &&
    `
      flex: 0 0 auto;
      margin: 0;
      white-space: nowrap;
      color: #61746f;

      > span {
        color: inherit !important;
      }

      svg {
        display: block;
        flex: 0 0 auto;
      }
    `}
`;
const ChangeHighlight = ({
  previous,
  current,
  isTablesUsed = false,
  isZones = false,
  isTime = false,
  isWaitingList = false,
  forceChanged,
  showChanges = true,
  t
}) => {
  const calculatedChanged = previous !== undefined && String(previous) !== String(current);
  const hasChanged = showChanges && (typeof forceChanged === "boolean" ? forceChanged : calculatedChanged);
  const isPreviousUndefined = previous === undefined || previous === null || previous === "";
  const isCurrentUndefined = current === undefined || current === null || current === "";
  const displayPrevious = isTablesUsed && isPreviousUndefined ? t("ui.i18nFinalCleanup.noTables") : isZones && isPreviousUndefined ? t("ui.i18nFinalCleanup.roomNotDefined") : previous;
  const displayCurrent = isTablesUsed && isCurrentUndefined ? t("ui.i18nFinalCleanup.noTables") : isZones && isCurrentUndefined ? t("ui.i18nFinalCleanup.noRoom") : isTime && isWaitingList ? t("ui.i18nFinalCleanup.registeredTime", { time: current }) : current;
  return <span style={{
    backgroundColor: hasChanged ? CHANGE_BACKGROUND : "transparent",
    padding: "1px 4px",
    borderRadius: "4px",
    color: hasChanged ? AFTER_COLOR : BEFORE_COLOR,
    fontWeight: hasChanged ? "500" : "normal"
  }}>
      {hasChanged && <span style={{
      color: BEFORE_COLOR
    }}>{displayPrevious} → </span>}
      {displayCurrent}
    </span>;
};
const StatusHighlight = ({
  previous,
  current,
  showChanges = true
}) => {
  const changed = showChanges && previous !== undefined && previous !== null && String(previous) !== String(current);
  const prevText = getReservationStatusAbreviationTranslation(previous);
  const currText = getReservationStatusAbreviationTranslation(current);
  const currColor = changed ? AFTER_COLOR : getStatusGroupColor(current, BEFORE_COLOR);
  return <>
      {changed && <span style={{
      color: BEFORE_COLOR,
      marginRight: "5px"
    }}>
          {prevText} →
        </span>}

      <span style={{
      color: currColor,
      fontWeight: changed ? "500" : "normal"
    }}>
        {currText}
      </span>
    </>;
};
const ChangeDateHighlight = ({
  previous,
  current,
  showChanges = true,
  t
}) => {
  const hasChanged = showChanges && previous !== undefined && String(previous) !== String(current);
  const isPreviousEmpty = previous === undefined || previous === null || previous === "";
  const isCurrentEmpty = current === undefined || current === null || current === "";
  const displayPrevious = isPreviousEmpty ? t("ui.i18nFinalCleanup.dateNotDefined") : previous;
  const displayCurrent = isCurrentEmpty ? t("ui.i18nFinalCleanup.dateNotDefined") : current;
  return <span style={{
    backgroundColor: hasChanged ? CHANGE_BACKGROUND : "transparent",
    padding: "1px 4px",
    borderRadius: "4px",
    color: hasChanged ? AFTER_COLOR : BEFORE_COLOR,
    fontWeight: hasChanged ? "500" : "normal"
  }}>
      {hasChanged && <span style={{
      color: BEFORE_COLOR
    }}>
          <DefaultDateUpper date={displayPrevious} /> →{" "}
        </span>}

      <span style={{
      color: hasChanged ? AFTER_COLOR : BEFORE_COLOR
    }}>
        <DefaultDateUpper date={displayCurrent} />
      </span>
    </span>;
};
function ReservationItems({
  reservation,
  all_tables,
  previousReservationFromHistory,
  isReservationHistory = false,
  isReservationFromRestaurantScreen = false,
  isNotifications = false,
  tableCountOnly = false,
  // Asks for the one-line detail row without claiming to be the reservations
  // screen, so the client history can share that screen's card design.
  compact = false
}) {
  const {
    t
  } = useTranslation();
  const reservationZone = useSelector(state => state.reservationZone);
  const {
    zones
  } = reservationZone;
  const tablesConfigList = useSelector(state => state.connectTablesConfig.connectTablesConfig);
  if (!reservation || typeof reservation !== "object" || Object.keys(reservation).length === 0) {
    return null;
  }
  const isCompact = isReservationFromRestaurantScreen || isNotifications || compact;
  const shouldShowChanges = !isCompact;
  const previousReservation = shouldShowChanges ? isReservationHistory ? previousReservationFromHistory : reservation.history?.[1] : null;
  const getPrev = key => previousReservation?.[key];
  const getCurr = key => key === "table_place_preference" && !isReservationHistory && reservation ? reservation.table_place_preference?.id : reservation?.[key];
  const hasBabyChairVisibleValue = Number(getCurr("baby_chair") || 0) !== 0 || Number(getPrev("baby_chair") || 0) !== 0;
  const hasAccessibilityVisibleValue = getCurr("accessability") === "wheel_chair" || getPrev("accessability") === "wheel_chair";
  // Only asked where the restaurant turned the question on, so a row that has
  // never been asked simply has nothing to show here.
  const hasStrollerVisibleValue = Boolean(getCurr("brings_stroller")) || Boolean(getPrev("brings_stroller"));
  const hasPetVisibleValue = Boolean(getCurr("brings_pet")) || Boolean(getPrev("brings_pet"));
  const isCancelledStatus = ["Cancelled", "cancelled", "cancelled_customer", "cancelled_restaurant"].includes(reservation.status);
  const calcConfigSum = (numOfTables, configList = []) => {
    if (!Array.isArray(configList) || numOfTables < 2) return 0;
    const chairsByTableCount = configList.reduce((acc, item) => {
      const tableCount = Number(item?.number_of_tables);
      const chairs = Number(item?.number_of_chairs) || 0;
      if (!Number.isNaN(tableCount)) {
        acc[tableCount] = chairs;
      }
      return acc;
    }, {});
    let configSum = 0;
    if (numOfTables >= 2) configSum += chairsByTableCount[2] || 0;
    if (numOfTables >= 3) configSum += chairsByTableCount[3] || 0;
    if (numOfTables >= 4) configSum += chairsByTableCount[4] || 0;
    for (let i = 5; i <= numOfTables; i++) {
      configSum += chairsByTableCount[(i + 1) % 2 + 1] || 0;
    }
    return configSum;
  };
  const parseTableIdsFromString = tablesString => {
    if (!tablesString) return [];
    return tablesString.split(",").map(n => parseInt(n, 10)).filter(n => !Number.isNaN(n)).sort((a, b) => a - b);
  };
  const getTablesSummaryFromIds = (tableIds, allTables, configList) => {
    if (!Array.isArray(allTables) || !Array.isArray(tableIds) || tableIds.length === 0) {
      return {
        tableCount: 0,
        baseSeats: 0,
        configSum: 0,
        totalChairs: 0,
        label: "—"
      };
    }
    const filteredTables = allTables.filter(t => tableIds.includes(t.id));
    const baseSeats = filteredTables.reduce((acc, t) => acc + (Number.parseInt(t.number_of_seats, 10) || 0), 0);
    const tableCount = filteredTables.length;
    const configSum = calcConfigSum(tableCount, configList);
    const totalChairs = baseSeats + configSum;
    return {
      tableCount,
      baseSeats,
      configSum,
      totalChairs,
      label: t("ui.i18nFinalCleanup.tablesAndChairs", { tables: tableCount, chairs: totalChairs })
    };
  };
  const parseTablesSummary = tablesString => {
    if (!tablesString || !all_tables) return "";
    const ids = parseTableIdsFromString(tablesString);
    const summary = getTablesSummaryFromIds(ids, all_tables, tablesConfigList);
    return tableCountOnly ? summary.tableCount : summary.label;
  };
  const getPhoneConfirmationText = (value, phoneNumber) => {
    if (!phoneNumber || phoneNumber.trim() === "" || phoneNumber.trim().length <= 3) {
      return <Tooltip text={t("ui.i18nFinalCleanup.noPhoneNumber")}>
          <NoPhoneTwoLinesSvgIcon width={18} height={16} strokeColor={BEFORE_COLOR} />
        </Tooltip>;
    }
    if (value === true) {
      return <Tooltip text={t("ui.i18nFinalCleanup.phoneConfirmed")}>
          <PhoneSvgIcon width={18} height={16} strokeColor={BEFORE_COLOR} />
        </Tooltip>;
    }
    return <Tooltip text={t("ui.i18nFinalCleanup.phoneNotConfirmed")}>
        <NoPhoneSvgIcon width={18} height={16} strokeColor={BEFORE_COLOR} />
      </Tooltip>;
  };
  function formatPhoneNumber(phone, countryCode) {
    if (!phone || !countryCode) return phone;
    const cleanCode = countryCode.replace(/\D/g, "");
    const codeLength = cleanCode.length;
    if (phone.startsWith(cleanCode)) {
      return `+${cleanCode} ${phone.slice(codeLength)}`;
    }
    return `+${cleanCode} ${phone}`;
  }
  return <Card $compact={isCompact}>
      <Box $compact={isCompact}>
        {/* Nome */}
        {isReservationHistory && <BoxItems>
            <LuUser />
            <ChangeHighlight t={t} showChanges={shouldShowChanges} previous={getPrev("full_name")} current={getCurr("full_name")} />
          </BoxItems>}

        {/* Telemóvel e Confirmação */}
        <BoxRow $compact={isCompact}>
          {isReservationHistory && <BoxItems>
              <ChangeHighlight t={t} showChanges={shouldShowChanges} forceChanged={String(getPrev("is_phone_confirmed")) !== String(getCurr("is_phone_confirmed")) || String(getPrev("phone_number") || "") !== String(getCurr("phone_number") || "")} previous={getPhoneConfirmationText(getPrev("is_phone_confirmed"), getPrev("phone_number"))} current={getPhoneConfirmationText(getCurr("is_phone_confirmed"), getCurr("phone_number"))} />

              <ChangeHighlight t={t} showChanges={shouldShowChanges} previous={formatPhoneNumber(getPrev("phone_number"), previousReservationFromHistory?.country_code)} current={formatPhoneNumber(getCurr("phone_number"), reservation?.country_code)} />
            </BoxItems>}

          {/* Data */}
          {!isReservationFromRestaurantScreen && <BoxItems $compact={isCompact} data-reservation-detail="date">
              <LuCalendar />
              {isNotifications
                ? <span><DefaultDateWeekday date={getCurr("date")} /></span>
                : <ChangeDateHighlight t={t} showChanges={shouldShowChanges} previous={getPrev("date")} current={getCurr("date")} />}
            </BoxItems>}
        </BoxRow>

        <BoxRow $compact={isCompact}>
          {/* Hora */}
          <BoxItems $compact={isCompact}>
            <LuClock />
            <ChangeHighlight t={t} showChanges={shouldShowChanges} isTime={true} isWaitingList={reservation.waiting_list_number != null} previous={getPrev("time")} current={getCurr("time")} />
          </BoxItems>

          {/* Nº de Pessoas */}
          <BoxItems $compact={isCompact}>
            <LuUsers />
            <ChangeHighlight t={t} showChanges={shouldShowChanges} previous={getPrev("number_of_people")} current={getCurr("number_of_people")} />
          </BoxItems>

          {/* Cadeira de bebé */}
          {hasBabyChairVisibleValue && <BoxItems $compact={isCompact}>
              <LuBaby />
              <ChangeHighlight t={t} showChanges={shouldShowChanges} previous={getPrev("baby_chair")} current={getCurr("baby_chair")} />
            </BoxItems>}
        </BoxRow>

        <BoxRow $compact={isCompact}>
          {/* Zona */}
          <BoxItems $compact={isCompact}>
            <LuLayoutGrid />
            <ChangeHighlight t={t} showChanges={shouldShowChanges} previous={zones?.zones_with_id?.[getPrev("table_place_preference")]} current={zones?.zones_with_id?.[getCurr("table_place_preference")]} isZones={true} />
          </BoxItems>
        </BoxRow>

        <BoxRow $compact={isCompact}>
          {/* Mesas usadas */}
          {(getCurr("tablesused") || getPrev("tablesused")) && all_tables && !isCancelledStatus && <BoxItems data-reservation-detail="tables" $compact={isCompact}>
                <DiningTableIcon />
                <ChangeHighlight t={t} showChanges={shouldShowChanges} previous={parseTablesSummary(getPrev("tablesused"))} current={parseTablesSummary(getCurr("tablesused"))} isTablesUsed={true} />
              </BoxItems>}

          {/* Erro: mesas insuficientes */}
          {reservation.not_enough_seats && reservation.waiting_list_number === null && <BoxItems style={{
          backgroundColor: CHANGE_BACKGROUND,
          borderRadius: "4px",
          padding: "4px 6px"
        }}>
                <LuAlertTriangle style={{
            color: AFTER_COLOR,
            fontSize: ".8rem",
            marginRight: "6px"
          }} />
                <span style={{
            color: AFTER_COLOR,
            fontSize: ".8rem"
          }}>{t("ui.components.reservations.reservationItems.mesasInsuficientes")}</span>
              </BoxItems>}
        </BoxRow>

        {/* Estado */}
        {isReservationHistory && <BoxItems>
            <LuCheck />
            <StatusHighlight showChanges={shouldShowChanges} previous={getPrev("status")} current={getCurr("status")} />
          </BoxItems>}

        {/* Acessibilidade e extras. Inside Box, like every other row: out here
            it was a sibling of Box, so display:contents handed these to Card -
            a 180px block column - and each one became its own line under the
            row instead of joining it. */}
        <BoxRow $compact={isCompact}>
        {hasAccessibilityVisibleValue && <BoxItems $compact={isCompact}>
            <LuAccessibility />
            <ChangeHighlight t={t} showChanges={shouldShowChanges} previous={getPrev("accessability") === "wheel_chair" ? t("ui.i18nCleanup2.mobRed") : t("ui.i18nFinalCleanup.none")} current={getCurr("accessability") === "wheel_chair" ? t("ui.i18nCleanup2.mobRed") : t("ui.i18nFinalCleanup.none")} />
          </BoxItems>}

        {/* Carrinho de bebé. There is no pram in the icon set this card uses,
            so this one is drawn to match it: same box, same 2px stroke, same
            currentColor, rather than the thinner one the booking form uses. */}
        {hasStrollerVisibleValue && <BoxItems $compact={isCompact}>
            <StrollerSvgIcon width="1em" height="1em" strokeWidth={2.5} strokeColor="currentColor" style={{ flex: "0 0 auto" }} />
            <ChangeHighlight t={t} showChanges={shouldShowChanges} previous={getPrev("brings_stroller") ? t("ui.components.stepThreeReservations.strollerExtra") : t("ui.i18nFinalCleanup.none")} current={getCurr("brings_stroller") ? t("ui.components.stepThreeReservations.strollerExtra") : t("ui.i18nFinalCleanup.none")} />
          </BoxItems>}

        {/* Cão - the card's own set has this one, so it uses it. */}
        {hasPetVisibleValue && <BoxItems $compact={isCompact}>
            <LuDog />
            <ChangeHighlight t={t} showChanges={shouldShowChanges} previous={getPrev("brings_pet") ? t("ui.components.stepThreeReservations.petExtra") : t("ui.i18nFinalCleanup.none")} current={getCurr("brings_pet") ? t("ui.components.stepThreeReservations.petExtra") : t("ui.i18nFinalCleanup.none")} />
          </BoxItems>}
        </BoxRow>
      </Box>
    </Card>;
}
/* Memoised: the reservation list renders every row on any change to the
   selection, so a row whose booking is unchanged must not pay for it. */
export default React.memo(ReservationItems);
